import * as React from 'react';

import * as styles from './ProjectModal.css';
import { Button, FormField, Input, MaskedInput, ModalWindow, Textarea } from '@cian/ui-kit';

const INVALID_CHARS_REGEX = /[^a-zA-Z0-9-]/;
const VALID_FORMAT_REGEX = /^[a-zA-Z0-9]+(-[a-zA-Z0-9]+)*$/;

export type TProjectData = {
  title: string;
  description: string;
  seoPath: string;
  seoTitle: string;
  seoDescription: string;
};

interface IProjectModalProps {
  isNew: boolean;
  isLoading: boolean;
  title: string;
  description: string;
  seoPath: string;
  seoTitle: string;
  seoDescription: string;
  onClose: () => void;
  onConfirm: (projectData: TProjectData) => void;
}

export const ProjectModal: React.FC<IProjectModalProps> = ({
  isNew,
  isLoading,
  title,
  description,
  seoPath,
  seoTitle,
  seoDescription,
  onClose,
  onConfirm,
}) => {
  const [projectTitle, setProjectTitle] = React.useState(title);
  const [projectSeoPath, setProjectSeoPath] = React.useState(seoPath);
  const [projectSeoTitle, setProjectSeoTitle] = React.useState(seoTitle);
  const [projectSeoDescription, setProjectSeoDescription] = React.useState(seoDescription);
  const [isSeoPathInvalid, setIsSeoPathInvalid] = React.useState(false);
  const [seoPathErrorMessage, setSeoPathErrorMessage] = React.useState<string | null>(null);
  const [projectDescription, setProjectDescription] = React.useState(description);

  const validateSeoPath = React.useCallback((value: string): { isValid: boolean; errorMessage: string | null } => {
    if (!value) {
      return { isValid: false, errorMessage: null };
    }

    if (INVALID_CHARS_REGEX.test(value)) {
      return {
        isValid: false,
        errorMessage: 'Используйте только латинские буквы, цифры и дефисы. Формат: sample-value',
      };
    }

    if (!VALID_FORMAT_REGEX.test(value)) {
      return { isValid: false, errorMessage: 'Некорректный формат. Не должен начинаться или заканчиваться дефисом' };
    }

    return { isValid: true, errorMessage: null };
  }, []);

  React.useEffect(() => {
    if (seoPath) {
      const validation = validateSeoPath(seoPath);
      setIsSeoPathInvalid(!validation.isValid);
      setSeoPathErrorMessage(validation.errorMessage);
    }
  }, [seoPath, validateSeoPath]);

  const onConfirmClick = () => {
    onConfirm({
      title: projectTitle,
      description: projectDescription,
      seoPath: projectSeoPath,
      seoTitle: projectSeoTitle,
      seoDescription: projectSeoDescription,
    });
  };

  const isConfirmDisabled = !projectTitle || !projectSeoPath || isLoading || isSeoPathInvalid;

  return (
    <ModalWindow
      title={isNew ? 'Создать проект' : 'Редактировать проект'}
      fixed={false}
      open
      size="M"
      width={500}
      onClose={onClose}
      footer={
        <div className={styles['footer']}>
          <Button onClick={onClose} size="M" theme="fill_white_primary">
            Отменить
          </Button>
          <Button
            loading={isLoading}
            onClick={onConfirmClick}
            size="M"
            theme="fill_primary"
            disabled={isConfirmDisabled}
          >
            {isNew ? 'Создать' : 'Сохранить'}
          </Button>
        </div>
      }
    >
      <div className={styles['form-container']}>
        <FormField required errorMessage={projectTitle ? null : 'Обязательное поле'} label="Название">
          <Input
            invalid={!projectTitle}
            value={projectTitle}
            onChange={(_, value) => {
              setProjectTitle(value);
            }}
          />
        </FormField>
        <FormField
          required
          errorMessage={projectSeoPath ? seoPathErrorMessage : 'Обязательное поле'}
          label="Название для ссылки. Например: sample-value"
        >
          <MaskedInput
            //@ts-expect-error нужен RegExp
            mask={/[a-zA-Z0-9-]/}
            lazy
            placeholderChar=" "
            placeholder="sample-value"
            invalid={!projectSeoPath || isSeoPathInvalid}
            value={projectSeoPath}
            onChange={value => {
              setProjectSeoPath(value);
              const validation = validateSeoPath(value);
              setIsSeoPathInvalid(!validation.isValid);
              setSeoPathErrorMessage(validation.errorMessage);
            }}
            preprocessInputChars={inputChat => {
              return inputChat === ' ' ? '-' : inputChat;
            }}
          />
        </FormField>
        <FormField required errorMessage={projectSeoTitle ? null : 'Обязательное поле'} label="SEO-название">
          <Input
            invalid={!projectSeoTitle}
            value={projectSeoTitle}
            onChange={(_, value) => {
              setProjectSeoTitle(value);
            }}
          />
        </FormField>
        <FormField withoutMargin label="Описание проекта">
          <Textarea
            value={projectDescription}
            minRows={4}
            onChange={(_, value) => {
              setProjectDescription(value);
            }}
          />
        </FormField>
        <FormField withoutMargin label="SEO-описание проекта">
          <Textarea
            value={projectSeoDescription}
            minRows={4}
            onChange={(_, value) => {
              setProjectSeoDescription(value);
            }}
          />
        </FormField>
      </div>
    </ModalWindow>
  );
};
